"use client";

import { useState, useSyncExternalStore } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { StarPicker } from "@/components/landing/star-rating";

const STORAGE_KEY = "ek_testimonial_submitted";

function subscribeNoop() {
  return () => {};
}

function getSubmittedSnapshot() {
  return window.localStorage.getItem(STORAGE_KEY) === "1";
}

function getSubmittedServerSnapshot() {
  return false;
}

/**
 * Bewertungsformular unter TestimonialsSection (backlog.md -> Feedback-Runde 19 Punkt 38).
 * Startet eingeklappt als simpler Button, um die Sektion nicht zu ueberladen. Nach
 * erfolgreichem Absenden merkt sich der Browser das per localStorage (reine Hoeflichkeits-
 * bremse gegen beilaeufiges Mehrfach-Absenden, keine Sicherheitsgrenze - die eigentliche
 * Absicherung gegen Missbrauch ist der Freigabe-Workflow in app/api/testimonials/route.ts).
 */
export function ReviewForm() {
  const [open, setOpen] = useState(false);
  const [author, setAuthor] = useState("");
  const [email, setEmail] = useState("");
  const [quote, setQuote] = useState("");
  const [rating, setRating] = useState(0);
  const [website, setWebsite] = useState(""); // Honeypot, siehe app/api/testimonials/route.ts
  const [status, setStatus] = useState<"idle" | "loading" | "sent" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // useSyncExternalStore statt Render-Phase-Update: liest localStorage
  // hydration-sicher aus - liefert waehrend SSR/dem allerersten Client-Render immer
  // den Server-Snapshot (false), erst danach den echten Wert. Ein Render-Phase-Update
  // wie in components/search/search-hero.tsx passt hier NICHT (echter Bug, live per
  // Hydration-Mismatch-Fehler gefunden): dessen Muster ist offiziell nur fuer "State an
  // eine geaenderte PROP anpassen" gedacht, nicht fuer einen Browser-Speicher, den es
  // serverseitig gar nicht gibt - der Server kann "true" nie rendern, ein sofortiges
  // Render-Phase-Update auf "true" im Browser weicht dadurch zwangslaeufig vom
  // Server-HTML ab.
  const previouslySubmitted = useSyncExternalStore(subscribeNoop, getSubmittedSnapshot, getSubmittedServerSnapshot);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (rating === 0) {
      setErrorMessage("Bitte eine Sternebewertung auswählen.");
      setStatus("error");
      return;
    }
    setStatus("loading");
    setErrorMessage(null);
    try {
      const res = await fetch("/api/testimonials", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ author, email, quote, rating, website }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        setErrorMessage(data?.error ?? null);
        setStatus("error");
        return;
      }
      window.localStorage.setItem(STORAGE_KEY, "1");
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  }

  if (previouslySubmitted || status === "sent") {
    return (
      <p className="text-sm text-ink-secondary">Danke für deine Bewertung! Wir freuen uns über dein Feedback.</p>
    );
  }

  if (!open) {
    return (
      <Button variant="outline" onClick={() => setOpen(true)}>
        Eigene Bewertung abgeben
      </Button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex w-full max-w-md flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-ink">Deine Bewertung</span>
        <StarPicker value={rating} onChange={setRating} />
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="review-author" className="text-sm font-medium text-ink">
          Name
        </label>
        <Input
          id="review-author"
          required
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
          placeholder="Dein Name"
          className="bg-card"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="review-email" className="text-sm font-medium text-ink">
          E-Mail-Adresse
        </label>
        <Input
          id="review-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="du@beispiel.de"
          className="bg-card"
        />
        <p className="text-xs text-muted-foreground">
          Wird nicht veröffentlicht - nur zur Bestätigung und gegen Mehrfach-Bewertungen.
        </p>
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="review-quote" className="text-sm font-medium text-ink">
          Deine Meinung
        </label>
        <Textarea
          id="review-quote"
          required
          rows={4}
          value={quote}
          onChange={(e) => setQuote(e.target.value)}
          placeholder="Was gefällt dir an Erikonomie?"
          className="bg-card"
        />
      </div>
      {/* Honeypot - fuer sehende Nutzer unsichtbar und ohne Tab-Stop, gleiches Muster wie
          components/contact-form.tsx. */}
      <input
        type="text"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute left-[-9999px] h-0 w-0 opacity-0"
      />
      <Button type="submit" disabled={status === "loading"}>
        {status === "loading" ? "Wird gesendet…" : "Bewertung absenden"}
      </Button>
      {status === "error" && (
        <p className="text-xs text-negative">
          {errorMessage ?? "Das hat gerade nicht geklappt. Bitte später erneut versuchen."}
        </p>
      )}
      <p className="text-xs text-muted-foreground">Vielen Dank – deine Bewertung wird in Kürze hier sichtbar.</p>
    </form>
  );
}
